// @ts-check
// "Identidade e envio" do e-mail de marketing (spec/email.md seções 9 e 19.5): cor da escola (com a prévia de um botão),
// nome do remetente, endereço de envio e de resposta, rodapé, domínios do site para o UTM, textos do motivo do envio,
// medir aberturas e cliques (com a explicação da LGPD) e limite diário até o teto da plataforma. Sem a flag WORKSPACE,
// tudo somente leitura. A frente 2A acrescenta o uso do dia, a suspensão e o domínio próprio de envio na Amazon SES
// (decisão P4 do usuário; sending-domain-section.js).
import { html } from 'htm/preact';
import { useEffect, useState } from 'preact/hooks';
import { updateEmailSettings } from '../../data/email-templates.js';
import { ApiError } from '../../data/api-client.js';
import { hasCurrentPermissionFlag } from '../../data/metadata-store.js';
import { Button } from '../../ui/button.js';
import { Icon } from '../../ui/icon.js';
import { Skeleton } from '../../ui/feedback.js';
import { showErrorSnackbar, showSuccessSnackbar } from '../../ui/snackbar.js';
import { formatNumber } from '../../lib/format.js';
import { EmailDialog } from '../email-design/email-dialog.js';
import { ColorField, PropertyRow, TextField, ToggleField } from '../email-design/property-controls.js';
import { emailSettingsState, loadEmailSettings, replaceEmailSettingsLocally, useEmailBranding } from '../email-design/email-branding.js';
import { SendingDomainSection } from './sending-domain-section.js';
import { hasTranslation, t } from '../../i18n/index.js';

/** @typedef {import('../../data/email-templates.js').EmailMarketingSettings} EmailMarketingSettings */

/**
 * Campos que mudaram (o PATCH leva só eles).
 * @param {EmailMarketingSettings} saved @param {EmailMarketingSettings} draft
 * @returns {Partial<EmailMarketingSettings>}
 */
export function changedEmailSettings(saved, draft) {
  /** @type {Record<string, unknown>} */
  const patch = {};
  for (const key of /** @type {Array<keyof EmailMarketingSettings>} */ (Object.keys(draft))) {
    if (JSON.stringify(draft[key]) !== JSON.stringify(saved[key])) patch[key] = draft[key];
  }
  return /** @type {Partial<EmailMarketingSettings>} */ (patch);
}

/** Motivo da suspensão em pt-BR. @param {string} reason */
const suspensionReasonText = (reason) => (hasTranslation(`emailSettings.suspendedReasons.${reason}`) ? t(`emailSettings.suspendedReasons.${reason}`) : reason);

/** Domínios digitados separados por vírgula, espaço ou linha. @param {string} text */
export const parseSiteDomains = (text) => [...new Set(text.split(/[\s,;]+/).map((domain) => domain.trim().toLowerCase()).filter(Boolean))];

/**
 * @param {{ onClose: () => void }} props
 */
export function EmailSettingsDialog({ onClose }) {
  const branding = useEmailBranding();
  const response = emailSettingsState.value;
  const [draft, setDraft] = useState(/** @type {EmailMarketingSettings | null} */ (response?.settings ?? null));
  const [domainsText, setDomainsText] = useState((response?.settings.siteDomains ?? []).join(', '));
  const [fieldErrors, setFieldErrors] = useState(/** @type {Record<string, string>} */ ({}));
  const [isSaving, setIsSaving] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const canEdit = Boolean(response?.canEdit) && hasCurrentPermissionFlag('WORKSPACE');
  // uso do dia em e-mails de marketing (contador do D1, frente 2A)
  const usage = response?.usageToday ?? null;

  useEffect(() => {
    loadEmailSettings({ force: true }).then((loaded) => {
      if (!loaded) {
        setLoadFailed(true);
        return;
      }
      setDraft(loaded.settings);
      setDomainsText(loaded.settings.siteDomains.join(', '));
    });
  }, []);

  /** @template {keyof EmailMarketingSettings} TKey @param {TKey} key @param {EmailMarketingSettings[TKey]} value */
  const set = (key, value) => {
    setDraft((current) => (current ? { ...current, [key]: value } : current));
    setFieldErrors((current) => ({ ...current, [key]: '' }));
  };

  const save = async () => {
    if (!draft || !response) return;
    const next = { ...draft, siteDomains: parseSiteDomains(domainsText) };
    const patch = changedEmailSettings(response.settings, next);
    if (Object.keys(patch).length === 0) {
      onClose();
      return;
    }
    setIsSaving(true);
    try {
      const result = await updateEmailSettings(patch);
      replaceEmailSettingsLocally({ ...response, settings: result.settings });
      showSuccessSnackbar(t('emailSettings.saved'));
      onClose();
    } catch (error) {
      if (error instanceof ApiError && typeof error.details?.field === 'string') {
        const field = error.details.field;
        setFieldErrors((current) => ({
          ...current,
          [field]: error.code === 'DAILY_LIMIT_ABOVE_PLATFORM'
            ? t('emailSettings.errors.dailyLimitAbovePlatform', { limit: formatNumber(response.platformDailyLimit) })
            : t(`emailSettings.errors.${field}`),
        }));
      } else showErrorSnackbar(error);
    } finally {
      setIsSaving(false);
    }
  };

  const footer = canEdit
    ? html`<${Button} title=${t('common.cancel')} variant="secondary" size="small" onClick=${onClose} />
        <${Button} title=${t('emailSettings.save')} accent="blue" size="small" isLoading=${isSaving} disabled=${!draft} onClick=${save} />`
    : html`<${Button} title=${t('common.close')} variant="secondary" size="small" onClick=${onClose} />`;

  return html`<${EmailDialog} title=${t('emailSettings.title')} subtitle=${t('emailSettings.subtitle')} onClose=${onClose} size="large" className="email-settings-dialog"
    footer=${footer} testId="email-settings-dialog">
    ${response?.suspension
      ? html`<div class="email-suspended-banner" role="alert">
          <${Icon} name="IconAlertTriangle" size=${16} />
          <div><strong>${t('emailSettings.suspendedTitle')}</strong><p>${t('emailSettings.suspendedDescription', { reason: suspensionReasonText(response.suspension.reason) })}</p></div>
        </div>`
      : null}
    ${!canEdit && response ? html`<p class="email-readonly-banner"><${Icon} name="IconLock" size=${14} />${t('emailSettings.readOnly')}</p>` : null}
    ${loadFailed
      ? html`<p class="email-inline-error" role="alert">${t('emailTemplates.loadError')}</p>`
      : !draft || !response
        ? html`<div class="email-settings-grid">${Array.from({ length: 6 }, (_, index) => html`<${Skeleton} key=${index} height=${48} />`)}</div>`
        : html`<div class="email-settings-grid">
            <section class="email-settings-section">
              <h3>${t('emailSettings.sections.identity')}</h3>
              <${ColorField} label=${t('emailSettings.brandColor')} value=${draft.brandColor} disabled=${!canEdit}
                onChange=${(/** @type {string} */ color) => set('brandColor', color)} />
              <div class="email-brand-preview" aria-hidden="true">
                <span class="email-canvas-button" style=${{ background: draft.brandColor, color: '#ffffff', borderRadius: '6px' }}>${t('emailSettings.buttonPreview')}</span>
              </div>
              <${TextField} label=${t('emailSettings.fromName')} value=${draft.fromName} maxLength=${80} disabled=${!canEdit}
                placeholder=${branding.institutionName} hint=${t('emailSettings.fromNameHint')} error=${fieldErrors.fromName || null}
                onChange=${(/** @type {string} */ value) => set('fromName', value)} />
              <${PropertyRow} label=${t('emailSettings.sendingAddress')} hint=${t('emailSettings.sendingAddressCurrentHint')}>
                <span class="email-property-value email-mono">${response.sendingAddress}</span>
              <//>
              <${TextField} label=${t('emailSettings.replyTo')} value=${draft.replyToEmail} maxLength=${254} disabled=${!canEdit}
                placeholder=${response.replyToAddress} hint=${draft.replyToEmail ? t('emailSettings.replyToCustomHint') : t('emailSettings.replyToHint')}
                error=${fieldErrors.replyToEmail || null} onChange=${(/** @type {string} */ value) => set('replyToEmail', value)} />
            </section>
            <section class="email-settings-section">
              <h3>${t('emailSettings.sections.footer')}</h3>
              <${TextField} label=${t('emailSettings.footerAddress')} value=${draft.footerAddress} maxLength=${200} disabled=${!canEdit} error=${fieldErrors.footerAddress || null}
                onChange=${(/** @type {string} */ value) => set('footerAddress', value)} />
              <${TextField} label=${t('emailSettings.footerPhone')} value=${draft.footerPhone} maxLength=${40} disabled=${!canEdit} error=${fieldErrors.footerPhone || null}
                onChange=${(/** @type {string} */ value) => set('footerPhone', value)} />
              <${TextField} label=${t('emailSettings.footerWebsite')} value=${draft.footerWebsite} maxLength=${200} disabled=${!canEdit} placeholder="https://"
                error=${fieldErrors.footerWebsite || null} onChange=${(/** @type {string} */ value) => set('footerWebsite', value)} />
              <${TextField} label=${t('emailSettings.siteDomains')} value=${domainsText} disabled=${!canEdit} hint=${t('emailSettings.siteDomainsHint')}
                error=${fieldErrors.siteDomains || null} onChange=${setDomainsText} />
              <${TextField} label=${t('emailSettings.marketingReason')} value=${draft.marketingReasonText} multiline=${true} rows=${2} maxLength=${300} disabled=${!canEdit}
                hint=${t('emailSettings.reasonHint')} error=${fieldErrors.marketingReasonText || null}
                onChange=${(/** @type {string} */ value) => set('marketingReasonText', value)} />
              <${TextField} label=${t('emailSettings.serviceReason')} value=${draft.serviceReasonText} multiline=${true} rows=${2} maxLength=${300} disabled=${!canEdit}
                hint=${t('emailSettings.reasonHint')} error=${fieldErrors.serviceReasonText || null}
                onChange=${(/** @type {string} */ value) => set('serviceReasonText', value)} />
            </section>
            <section class="email-settings-section">
              <h3>${t('emailSettings.sections.sending')}</h3>
              <${ToggleField} label=${t('emailSettings.trackOpens')} value=${draft.trackOpens} disabled=${!canEdit} onChange=${(/** @type {boolean} */ value) => set('trackOpens', value)} />
              <${ToggleField} label=${t('emailSettings.trackClicks')} value=${draft.trackClicks} disabled=${!canEdit} onChange=${(/** @type {boolean} */ value) => set('trackClicks', value)} />
              <p class="input-hint">${t('emailSettings.trackingHint')}</p>
              <${PropertyRow} label=${t('emailSettings.dailyLimit')} htmlFor="email-daily-limit"
                hint=${t('emailSettings.platformLimit', { limit: formatNumber(response.platformDailyLimit) })} error=${fieldErrors.dailyLimit || null}>
                <input id="email-daily-limit" class="text-input" type="number" min="1" max=${response.platformDailyLimit} disabled=${!canEdit}
                  value=${draft.dailyLimit ?? ''} placeholder=${formatNumber(response.platformDailyLimit)}
                  onInput=${(/** @type {Event} */ event) => {
                    const text = /** @type {HTMLInputElement} */ (event.currentTarget).value.trim();
                    set('dailyLimit', text === '' ? null : Math.round(Number(text)));
                  }} />
              <//>
              ${usage
                ? html`<p class="email-muted" data-testid="email-usage-today">${t('emailSettings.usageToday', {
                    used: formatNumber(usage.school), limit: formatNumber(usage.schoolLimit),
                  })}</p>
                  <p class="email-muted">${t('emailSettings.usageCampaigns', { used: formatNumber(usage.schoolCampaigns ?? 0), limit: formatNumber(usage.schoolCampaignsLimit ?? 0) })}</p>`
                : null}
            </section>
            <${SendingDomainSection} platformAddress=${response.platformSendingAddress ?? response.sendingAddress}
              onChanged=${() => loadEmailSettings({ force: true })} />
          </div>`}
  <//>`;
}
